import ErrorHandler from "../error-handlers/axios/defaultErrorHandler";

/**
 * Fetches hot lead funnel data for a specific agent
 *
 * @param {Object} axiosInstance - Axios instance with authentication
 * @param {string} agentId - The ID of the agent to fetch hot lead funnel data for
 * @param {Object} options - Request options
 * @param {Object} options.params - Query parameters
 * @param {string} options.params.period - Time period ('daily', 'weekly', 'monthly')
 * @param {string} options.params.startDate - Start date in ISO format
 * @param {string} options.params.endDate - End date in ISO format
 * @returns {Promise<Object>} - Agent hot lead funnel data with the following structure:
 * {
 *   period: string,
 *   startDate: string,
 *   endDate: string,
 *   hotLeadsAssigned: number,
 *   hotLeadsFollowedUp: number,
 *   hotLeadsConverted: number,
 *   hotLeadsListingDeals: number,
 *   hotLeadsWon: number,
 *   hotLeadConversionRate: number,
 *   hotLeadDropOffRate: number,
 *   hotLeadSourceMetrics: {
 *     [sourceName: string]: {
 *       leadsAssigned: number,
 *       leadsFollowedUp: number,
 *       leadsConverted: number,
 *       listingDeals: number,
 *       leadsWon: number,
 *       conversionRate: number,
 *       dropOffRate: number
 *     }
 *   }
 * }
 */
export const getAgentHotLeadFunnel = async (axiosInstance, agentId, options = {}) => {
  try {
    // Ensure startDate and endDate are provided
    if (!options.params?.startDate || !options.params?.endDate) {
      const today = new Date();
      const defaultStartDate = new Date(today.getFullYear(), today.getMonth(), 1); // First day of current month
      const defaultEndDate = new Date(today.getFullYear(), today.getMonth() + 1, 0); // Last day of current month

      options.params = {
        ...options.params,
        startDate: options.params?.startDate || defaultStartDate.toISOString(),
        endDate: options.params?.endDate || defaultEndDate.toISOString()
      };
    }

    const res = await axiosInstance.get(`/agent-performance/hot-lead-funnel/${agentId}`, options);
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
    return {
      period: "monthly",
      startDate: "",
      endDate: "",
      hotLeadsAssigned: 0,
      hotLeadsFollowedUp: 0,
      hotLeadsConverted: 0,
      hotLeadsListingDeals: 0,
      hotLeadsWon: 0,
      hotLeadConversionRate: 0,
      hotLeadDropOffRate: 0,
      hotLeadSourceMetrics: {},
      error: true
    };
  }
};
